import {
	Form,
	MentionInput,
	MentionInputHandle,
	Portal,
	useMentionInputIntermediateState,
	MentionInputIntermediateStateHandlers,
	FileSelector,
	EmojiPicker,
	IconButton
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubCommentMediaKind,
	HubCommentMediaSaveModel,
	HubCommentModel,
	HubCommentSaveModel
} from "client/Api/Hub/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import styles from "./MobileCommentForm.module.less";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice,
	useIsMounted,
	useUnmountedCancellation
} from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	resolveFormattedTextString,
	getDataUrl,
} from "client/Misc/utils";
import { MobileCommentFormHandle, MobileCommentMode } from "../MobileComment";
import { MobileCommentAvatar } from "../MobileCommentAvatar/MobileCommentAvatar";
import {
	CommentFormData,
	createFormDataForEdit,
	createFormDataForReply,
	createSaveModel,
	EMPTY_COMMENT_FORM_DATA
} from "../../CommentForm/CommentForm";
import { getModel, userMentionType } from "client/Components/MentionInput/converters";
import { isProseMirror } from "client/Components/MentionInput/types";
import { HubCommentSectionService } from "../../HubCommentSection";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { tryParseServerErrors } from "client/Validation/serverErrors";
import { InsertSuggestionHandler, useInsertSuggestionHandler } from "client/Features/Hub/MentionSuggestionBox/useInsertSuggestionHandler";
import { MentionSuggestionBox } from "client/Features/Hub/MentionSuggestionBox/MentionSuggestionBox";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { InvalidInputValidationResult } from "client/Validation/input";
import { ModelPath } from "client/Validation/path";
import { never } from "client_legacy/ndaw/errors";
import { FormImageAttachment } from "client/Features/Hub/FormImageAttachment";
import { formatList } from "client/Misc/cultureSpecificUtils";

const rootTrackingId: string = "mobile-comment-form";
const MAX_INPUT_HEIGHT = 168;

export type MobileCommentFormProps = {
	onCreated?: (comment: HubCommentModel) => void;
	onUpdated: (comment: HubCommentModel) => void;
	onMobileIsEditingChanged: (isEditing: boolean) => void;
	service: HubCommentSectionService;
	currentUserInfo: UserIdentityModel;
	mode: MobileCommentMode;
	commentOptions: HubCommentOptionsModel;
};

export const MobileCommentForm = forwardRef(
	(
		props: MobileCommentFormProps,
		ref: Ref<MobileCommentFormHandle>
	): ReactElement => {
		const {
			service,
			currentUserInfo,
			mode,
			onCreated,
			onUpdated,
			onMobileIsEditingChanged,
			commentOptions
		} = props;

		const [isBusy, setIsBusy] = useState<boolean>(false);
		const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);

		const mentionInputRef: RefObject<MentionInputHandle | null> = useRef<MentionInputHandle>(null);
		const emojiButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
		const mentionSuggestionHost: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const intermediateState: MentionInputIntermediateStateHandlers = useMentionInputIntermediateState();
		const [formData, setFormData] = useState<CommentFormData>(EMPTY_COMMENT_FORM_DATA);
		const formHandler: IFormHandler<CommentFormData> = useFormHandler(
			formData,
			setFormData,
			"NoValidation"
		);

		const globalCancellation: AbortSignal = useUnmountedCancellation();
		const alertManager: AlertManager = useAlerts();
		const isMounted: () => boolean = useIsMounted();
		const isTouch: boolean = useIsTouchDevice();

		const deleteImageHandler = useCallback(
			(): void => {
				formHandler.for("media")
					.reducer
					.setValue({
						isSet: true,
						value: null
					});

				formHandler.for("media")
					.validation
					.triggerInputFinished();
			},
			[formHandler]
		);

		const formSubmitHandler = useCallback(
			async (): Promise<void> => {
				if (isBusy) {
					return;
				}

				setIsBusy(true);

				try {
					const validationResult: InputValidationResult = await formHandler.validation
						.validate(globalCancellation);

					if (validationResult.kind === InputValidationResultKind.Invalid) {
						setIsBusy(false);
						return;
					}

					const model: HubCommentSaveModel = createSaveModel(formData);

					if(mode.kind === "create"){
						const createCommentResult: HubCommentModel = await service.createComment(
							model,
							globalCancellation
						);

						onCreated?.(createCommentResult);
					} else if (mode.kind === "reply") {
						const createReplyResult: HubCommentModel = await service.createReply(
							mode.parentCommentId,
							model,
							globalCancellation
						);

						onCreated?.(createReplyResult);
					} else {
						const updateCommentResult: HubCommentModel = await service.editComment(
							mode.commentId,
							model,
							globalCancellation
						);
						onUpdated(updateCommentResult);
					}

					if (isMounted()) {
						setFormData(EMPTY_COMMENT_FORM_DATA);
						setIsBusy(false);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);

					if (
						formHandler.validation
							.tryProcessServerError(e)
					) {
						const result: TryResult<HubCommentServerError> = tryParseHubCommentServerError(e);
						if (!result.wasSuccessful) {
							return;
						}

						const messageResult: string | undefined = result.result
							.message;

						if (isNotNil(messageResult)) {
							alertManager.showError(messageResult);
						}

						const mediaResult: string | undefined = result.result
							.media;

						if (isNotNil(mediaResult)) {
							alertManager.showError(mediaResult);

							formHandler.for("media")
								.reducer
								.setValue({
									isSet: true,
									value: null
								});

							formHandler.for("media")
								.validation
								.triggerValueChanged();
						}
					} else {
						console.error(e);
						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				}
			},
			[
				alertManager,
				formData,
				formHandler,
				globalCancellation,
				isBusy,
				mode,
				onCreated,
				onUpdated,
				service,
				setFormData,
				isMounted
			]
		);

		const formText = useMemo(
			(): string | undefined => {
				return isProseMirror(intermediateState.intermediateState)
					? getModel(
						intermediateState.intermediateState
							.editorState
					).text
					: undefined;
			},
			[intermediateState]
		);

		const isEmpty: boolean = formText?.length === 0;
		const isMessageDifferent: boolean = mode.kind === "edit"
			? formText !== mode.message
			: true;

		const canSubmit = useMemo(
			(): boolean => {
				if (isBusy) {
					return false;
				}

				if (
					isEmpty
						&& formData.media.isSet
						&& formData.media.value === null
				) {
					return false;
				}

				if (mode.kind === "edit") {
					const { media } = formData;

					const isMediaDifferent: boolean = media.isSet;

					return isMessageDifferent || isMediaDifferent;
				}

				return true;
			},
			[
				formData,
				mode,
				isBusy,
				isEmpty,
				isMessageDifferent
			]
		);

		useEffect(
			() => {
				onMobileIsEditingChanged(canSubmit);

				if (canSubmit) {
					window.addEventListener("beforeunload", beforeUnloadListener);
				}

				return () => {
					onMobileIsEditingChanged(false);

					if (canSubmit) {
						window.removeEventListener("beforeunload", beforeUnloadListener);
					}
				};
			},
			[canSubmit, onMobileIsEditingChanged]
		);

		useImperativeHandle(
			ref,
			(): MobileCommentFormHandle => ({
				focusInput(): void {
					mentionInputRef.current
						?.focus();
				}
			}),
			[]
		);

		const insertSuggestionHandler: InsertSuggestionHandler = useInsertSuggestionHandler(
			service,
			setFormData
		);

		const imgSrc = useMemo(
			(): string | undefined => {
				const { media } = formData;

				if (!media.isSet) {
					if (mode.kind === "edit" && isNotNil(mode.media)) {
						return service.getCommentImageUrl(
							mode.media
								?.imageId
						);
					}

					return undefined;
				}

				const mediaValue: HubCommentMediaSaveModel | null = media.value;

				if (
					mediaValue?.image
						.isSet
				) {
					return mediaValue.image
						.value
						.uri;
				}

				return undefined;
			},
			[
				formData,
				mode,
				service
			]
		);

		const canSelectImage = useMemo(
			(): boolean => {
				const { media } = formData;

				if (!media.isSet) {
					if (mode.kind === "edit" && isNil(mode.media)) {
						return true;
					}

					return false;
				}

				if (
					isNotNil(media.value)
					&& media.value
						.image
						.isSet
				) {
					return false;
				}

				return true;
			},
			[
				formData,
				mode
			]
		);

		const selectImageHandler = useCallback(
			async (file: File): Promise<void> => {
				const selectedImageResult: TryResult<Base64FileUriUploadModel> = await manageCommentImageSelection(
					file,
					alertManager,
					commentOptions
				);

				if (!selectedImageResult.wasSuccessful) {
					return;
				}

				formHandler.for("media")
					.reducer
					.setValue({
							isSet: true,
							value: {
								mediaKind: HubCommentMediaKind.Image,
								image: {
									isSet: true,
									value: selectedImageResult.result
								}
							}
					});
			},
			[
				alertManager,
				commentOptions,
				formHandler
			]
		);

		const selectEmojiHandler = useCallback(
			(emojiUnicode: string): void => {
				mentionInputRef.current
					?.insert(emojiUnicode);

				setIsEmojiPickerOpen(false);

				mentionInputRef.current
					?.focus();
			},
			[]
		);

		useEffect(
			(): void => {
				let formData: CommentFormData;

				switch (mode.kind) {
					case "edit":
						formData = createFormDataForEdit(
							mode.message,
							mode.mentions
						);
						break;
					case "create":
						formData = EMPTY_COMMENT_FORM_DATA;
						break;
					case "reply":
						formData = createFormDataForReply(
							mode.authorUserInfo,
							currentUserInfo.userId
						);
						break;
					default:
						never(mode);
				}

				setFormData(formData);
			},
			[
				currentUserInfo.userId,
				mode,
				setFormData
			]
		);

		return (
			<>
				<div ref={mentionSuggestionHost} />
				{isNotNil(imgSrc) && (
					<div className={styles.imageContainer}>
						<FormImageAttachment
							src={imgSrc}
							onDelete={deleteImageHandler}
							variant="mobile"
							trackingId="image"
						/>
					</div>
				)}
				<Form
					onSubmit={formSubmitHandler}
					trackingId={rootTrackingId}
				>
					<div className={styles.formContent}>
						<div className={styles.avatarContainer}>
							<MobileCommentAvatar
								authorUserInfo={
									mode.kind !== "edit"
										? currentUserInfo
										: mode.authorUserInfo
								}
							/>
						</div>
						<div className={styles.message}>
							<MentionInput
								{...formHandler.for("message").attachToMentionInput(intermediateState, formData.mentionData)}
								height="auto"
								maxHeight={MAX_INPUT_HEIGHT}
								mentionTypes={userMentionType}
								placeholder={localize("_WriteYourComment")}
								ref={mentionInputRef}
								isBorderless
								isInvalid={false}
								renderSuggestionBox={(props, ref) => (
									<>
										{mentionSuggestionHost.current && (
											<Portal container={mentionSuggestionHost.current}>
												<MentionSuggestionBox
													{...props}
													ref={ref}
													findUsers={service.findUsers}
												/>
											</Portal>
										)}
									</>
								)}
								onInsertSuggestion={insertSuggestionHandler}
							/>
						</div>
						<div className={styles.buttons}>
							{!isTouch && (
								<IconButton
									ref={emojiButtonRef}
									trackingId={generateTrackingId(rootTrackingId, "__add-emoji")}
									onClick={(): void => setIsEmojiPickerOpen(x => !x)}
									icon="face-smile"
									kind="transparent-colored"
									height="fill"
									size="large"
									isActive={isEmojiPickerOpen}
								/>
							)}
							<FileSelector
								onChange={selectImageHandler}
								contentTypes={commentOptions.supportedImageTypes}
							>
								{(onClick) => (
									<IconButton
										trackingId={generateTrackingId(rootTrackingId, "__add-image")}
										onClick={onClick}
										isDisabled={!canSelectImage}
										icon="image"
										kind="transparent-colored"
										height="fill"
										size="large"
									/>
								)}
							</FileSelector>
							<IconButton
								trackingId={generateTrackingId(rootTrackingId, "__save-comment")}
								icon={
									(mode.kind === "create" || mode.kind === "reply")
										? "paper-plane"
										: "floppy-disk"
								}
								isDisabled={!canSubmit}
								onClick={formSubmitHandler}
								kind="transparent-colored"
								height="fill"
								size="large"
							/>
						</div>
					</div>
					{isEmojiPickerOpen
						&& isNotNil(emojiButtonRef.current)
						&& !isTouch
						&& (
							<EmojiPicker
								target={emojiButtonRef.current}
								onClose={(): void => setIsEmojiPickerOpen(false)}
								onSelected={selectEmojiHandler}
								trackingId={generateTrackingId(rootTrackingId, "__emoji-picker")}
							/>
						)
					}
				</Form>
			</>
		);
	}
);

MobileCommentForm.displayName = "MobileCommentForm";

function beforeUnloadListener(event: BeforeUnloadEvent): any {
	event.preventDefault();
	event.returnValue = localize("_YouHaveStartedWritingOrEditingAComment");
	return event.returnValue;
};

export async function manageCommentImageSelection(
	file: File,
	alertManager: AlertManager,
	commentOptions: HubCommentOptionsModel
): Promise<TryResult<Base64FileUriUploadModel>> {
	const { maxImageSize, supportedImageTypes } = commentOptions;

	if (!supportedImageTypes.includes(file.type)) {
		alertManager.showError(
			resolveFormattedTextString({
				template: localize("_SupportedImageFormats"),
				replacements: [
					{
						what: "{0}",
						filler: formatList([
								localize("_FileExtensionJpg"),
								localize("_FileExtensionPng"),
								localize("_FileExtensionGif")
						])
					}
				]
			})
		);

		return {
			wasSuccessful: false
		};
	}

	const dataUrl: string = await getDataUrl(file);

	if (file.size > maxImageSize) {
		alertManager.showError(
			resolveFormattedTextString({
				template: localize("_MaxSizeIs"),
				replacements: [
					{
						what: "{0}",
						filler: getDisplayFriendlySize(maxImageSize)
					}
				]
			})
		);

		return {
			wasSuccessful: false
		};
	}

	return {
		wasSuccessful: true,
		result: {
			filename: file.name,
			uri: dataUrl
		}
	};
}

export type HubCommentServerError = {
	media?: string;
	message?: string;
};

export function tryParseHubCommentServerError(
	error: unknown
): TryResult<HubCommentServerError> {
	const result: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(
		error,
		[
			{
				pattern: new DotPathWildcardPattern("Media.**"),
				replacement: "Media"
			},
			{
				pattern: new DotPathWildcardPattern("Message.**"),
				replacement: "Message"
			}
		]
	);

	if (!result.wasSuccessful) {
		return result;
	}

	return {
		wasSuccessful: true,
		result: {
			media: result.result
				.get(ModelPath.root.for("media"))
				?.errorMessage,
			message: result.result
				.get(ModelPath.root.for("message"))
				?.errorMessage
		}
	};
}